import type { BonDruck } from '../bonweg';
import { useCallback, useEffect, useState, useRef } from 'react';

import {
  ApiFehler,
  type BelegDetails,
  type JournalBeleg,
  type KassenApi,
  type Zahlung,
} from '../api';
import { aktiverBedienerName, aktuelleRechte, ladeAnmeldung } from '../bediener';
import { bekannterStorno, stornoBelegnummer } from '../belegnummern';
import { istRetourenbeleg, retoureKennzeichen } from '../retoure/api';
import { formatiereEuro, stornoPositionen, summeBrutto } from '../warenkorb';
import { Belegansicht } from './Belegansicht';
import { StornoGrundDialog } from './StornoGrundDialog';
import { ZahlungDialog } from './ZahlungDialog';

type StornoArt = 'bar' | 'karte' | 'gutschein';
/** R4: beim Öffnen des Grund-Dialogs eingefroren – ein Belegwechsel lenkt den Storno nicht um. */
interface StornoAuftrag {
  original: BelegDetails;
  art: StornoArt;
}

/**
 * Beleg suchen, nachdrucken, stornieren (Gegenbuchung, Regel 1). Bar: sofort
 * Stornobeleg. Karte: erst Rückerstattung am Terminal/Anbieter, erst mit
 * „erstattet" entsteht der Stornobeleg (Regel 3 für den Rückweg).
 */
export function Belege(props: {
  api: KassenApi;
  kassenId: string;
  bridgeDruck?: BonDruck;
  emailSenden?: (belegnummer: string, an: string, form?: 'bon' | 'rechnung') => Promise<string>;
  unternehmer?: { name: string; anschrift: string; uid?: string } | null;
  /**
   * Retoure (nur mit Geräteschalter): Rückgabestand eines Verkaufsbelegs über die Originalsuche
   * („teilweise zurückgegeben“); null = keine Rückgabe bzw. unbekannt.
   */
  rueckgabeStand?: (belegnummer: string) => Promise<string | null>;
}) {
  const { api, kassenId } = props;
  const [nummer, setNummer] = useState('');
  /** Zuletzt eingegebener Stornogrund – auch für den Stornobeleg nach einer Rückerstattung. */
  const stornoGrund = useRef<string | null>(null);
  /** R4: Original und Zahlungsart des laufenden Stornos – bis Rückerstattung und Wiederholung. */
  const stornoAuftrag = useRef<StornoAuftrag | null>(null);
  /** Offener Storno-Grund-Dialog (ersetzt `window.prompt`). */
  const [grundDialog, setGrundDialog] = useState<StornoAuftrag | null>(null);
  /** Storno wird verarbeitet – Knopf gesperrt (Doppeltipp). */
  const [stornoLaeuft, setStornoLaeuft] = useState(false);
  const [treffer, setTreffer] = useState<JournalBeleg[]>([]);
  const [trefferGesamt, setTrefferGesamt] = useState(0);
  const [suchtext, setSuchtext] = useState<string | null>(null);

  /** Letzte Belege bzw. Suchtreffer aus dem Journal (Belegnummer, Artikel, Notiz, Empfänger). */
  const suchen = useCallback(
    async (text?: string) => {
      try {
        const r = await api.belegjournal(kassenId, {
          ...(text === undefined || text.trim() === '' ? {} : { suche: text.trim() }),
          limit: 20,
        });
        setTreffer(r.belege);
        setTrefferGesamt(r.gesamt);
        setSuchtext(text === undefined || text.trim() === '' ? null : text.trim());
      } catch {
        setTreffer([]); // z. B. offline – die direkte Belegnummer geht weiterhin
      }
    },
    [api, kassenId],
  );
  useEffect(() => {
    void suchen();
  }, [suchen]);
  const [beleg, setBeleg] = useState<{ beleg: BelegDetails; text: string | null } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  /** Laufende Rückerstattung am Terminal (Dialog). */
  const [rueckerstattung, setRueckerstattung] = useState<Zahlung | null>(null);
  /** Erfolgte Rückerstattung, zu der der Stornobeleg noch fehlt (z. B. nach Netzfehler). */
  const [erstattet, setErstattet] = useState<Zahlung | null>(null);
  /**
   * A5-4: bekannter Stornobeleg des angezeigten Belegs (Journal bzw. 409 des Servers) – dann wird
   * kein Storno mehr angeboten, sondern „bereits storniert: <Nr>“ gezeigt.
   */
  const [storniertDurch, setStorniertDurch] = useState<string | null>(null);
  /** Rückgabestand des angezeigten Belegs (Retoure), gebunden an die Belegnummer. */
  const [rueckgabe, setRueckgabe] = useState<{ nr: string; text: string } | null>(null);

  async function laden(belegnummer = nummer): Promise<void> {
    setFehler(null);
    setMeldung(null);
    try {
      const b = await api.beleg(kassenId, belegnummer.trim());
      const text = await api.druck(kassenId, b.belegnummer).catch(() => null);
      // A5-4: schon storniert? (offline bzw. Fehler: unbekannt – der Server lehnt dann mit 409 ab)
      const storno =
        b.belegtyp === 'STANDARD'
          ? await api
              .belegjournal(kassenId, { suche: `ST-${b.belegnummer}`, limit: 50 })
              .then((j) => bekannterStorno(b, j.belege))
              .catch(() => null)
          : null;
      setStorniertDurch(storno);
      setBeleg({ beleg: b, text });
      setRueckgabe(null);
      if (
        props.rueckgabeStand !== undefined &&
        b.belegtyp === 'STANDARD' &&
        !istRetourenbeleg(b.belegnummer)
      ) {
        void props
          .rueckgabeStand(b.belegnummer)
          .then((t) => {
            if (t !== null) {
              setRueckgabe({ nr: b.belegnummer, text: t });
            }
          })
          .catch(() => undefined);
      }
    } catch (e: unknown) {
      setBeleg(null);
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }

  /**
   * Stornogrund abfragen (Pflicht; steht als Notiz am Stornobeleg): öffnet den Grund-Dialog mit
   * eingefrorenem Auftrag. Weiter geht es erst mit „Weiter zum Storno“ (`stornoFortsetzen`).
   */
  function grundAbfragen(original: BelegDetails, art: StornoArt): void {
    setFehler(null);
    setGrundDialog({ original, art });
  }

  /** R4: nach dem Grund – Gegenbuchung (Bar, Gutschein) bzw. Rückerstattung (Karte). */
  async function stornoFortsetzen(auftrag: StornoAuftrag, grund: string): Promise<void> {
    setGrundDialog(null);
    stornoGrund.current = grund;
    stornoAuftrag.current = auftrag;
    setStornoLaeuft(true);
    try {
      if (auftrag.art === 'karte') {
        await karteRueckerstatten(auftrag.original);
      } else {
        await stornobelegErstellen(auftrag.original, auftrag.art, undefined, grund);
      }
    } finally {
      setStornoLaeuft(false);
    }
  }

  /** R4: Erstversuch und Wiederholung des Stornobelegs nur einmal zugleich (Doppeltipp). */
  const belegSperre = useRef(false);
  const [belegLaeuft, setBelegLaeuft] = useState(false);
  async function stornobelegErstellen(
    original: BelegDetails,
    zahlungsart: 'bar' | 'karte' | 'gutschein',
    zahlungsreferenz?: string,
    grund: string | null = stornoGrund.current,
  ): Promise<void> {
    if (belegSperre.current) {
      return;
    }
    belegSperre.current = true;
    setBelegLaeuft(true);
    try {
      await stornobelegSenden(original, zahlungsart, zahlungsreferenz, grund);
    } finally {
      belegSperre.current = false;
      setBelegLaeuft(false);
    }
  }

  async function stornobelegSenden(
    original: BelegDetails,
    zahlungsart: 'bar' | 'karte' | 'gutschein',
    zahlungsreferenz: string | undefined,
    grund: string | null,
  ): Promise<void> {
    try {
      const storno = await api.erstelleBeleg(kassenId, {
        belegtyp: 'STORNO',
        belegnummer: stornoBelegnummer(original.belegnummer),
        positionen: stornoPositionen(original.positionen),
        zahlungsart,
        ...(grund === null
          ? {}
          : { notiz: `Storno ${original.belegnummer}: ${grund}`.slice(0, 200) }),
        ...(zahlungsreferenz === undefined ? {} : { zahlungsreferenz }),
        ...(original.empfaenger === null ? {} : { empfaenger: original.empfaenger }),
        // Gutscheine des Originals (Einlösungen, Verkäufe) werden am Server zurückgebucht.
        stornoVon: original.belegnummer,
        ...(() => {
          const aktiv = aktiverBedienerName(sessionStorage, localStorage, kassenId);
          return aktiv === null ? {} : { bediener: aktiv };
        })(),
        // Signierte Anmeldung: der Server prüft das Stornorecht daran, nicht am Namen.
        ...(() => {
          const sitzung = ladeAnmeldung(sessionStorage, kassenId)?.sitzung;
          return sitzung === undefined ? {} : { bedienerSitzung: sitzung };
        })(),
      });
      setErstattet(null);
      stornoGrund.current = null;
      stornoAuftrag.current = null;
      setMeldung(`Stornobeleg ${storno.belegnummer} erstellt`);
      await laden(storno.belegnummer);
    } catch (e: unknown) {
      // Schon storniert (S3: ein Storno je Original) – z. B. Antwort beim ersten Versuch
      // verloren: vorhandenen Stornobeleg zeigen statt eines Fehlers.
      const vorhanden =
        e instanceof ApiFehler && e.status === 409
          ? /bereits storniert \((\S+)\)/.exec(e.message)?.[1]
          : undefined;
      if (vorhanden !== undefined) {
        const s = await api.beleg(kassenId, vorhanden).catch(() => null);
        if (zahlungsreferenz === undefined || s?.zahlungsreferenz === zahlungsreferenz) {
          setErstattet(null);
          stornoGrund.current = null;
          stornoAuftrag.current = null;
          await laden(vorhanden);
          // A5-4: Ablehnung, kein Erfolg – in Fehlerfarbe (vorher grün wie ein neuer Storno)
          setFehler(
            `Beleg ${original.belegnummer} ist bereits storniert: ${vorhanden} – kein neuer Stornobeleg erstellt`,
          );
          return;
        }
        setStorniertDurch(vorhanden);
      }
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }

  function barStornieren(): void {
    if (beleg === null) {
      return;
    }
    const gutschein =
      beleg.beleg.gutscheinCent > 0
        ? ` (${formatiereEuro(beleg.beleg.gutscheinCent)} € zurück auf den Gutschein)`
        : '';
    if (
      !window.confirm(`Beleg ${beleg.beleg.belegnummer} stornieren (Gegenbuchung)${gutschein}?`)
    ) {
      return;
    }
    grundAbfragen(beleg.beleg, 'bar');
  }

  function gutscheinStornieren(): void {
    if (beleg === null) {
      return;
    }
    if (
      !window.confirm(
        `Beleg ${beleg.beleg.belegnummer} stornieren? Der Betrag wird dem Gutschein wieder gutgeschrieben.`,
      )
    ) {
      return;
    }
    grundAbfragen(beleg.beleg, 'gutschein');
  }

  function karteStornieren(): void {
    const zahlungsreferenz = beleg?.beleg.zahlungsreferenz ?? null;
    if (beleg === null || zahlungsreferenz === null) {
      return;
    }
    // Rückerstattung nur über den mit Karte bezahlten Rest; der Gutscheinanteil geht zurück auf den Gutschein.
    const summe = summeBrutto(beleg.beleg.positionen) - beleg.beleg.gutscheinCent;
    if (
      !window.confirm(
        `Kartenzahlung zu Beleg ${beleg.beleg.belegnummer} über ${formatiereEuro(summe)} € rückerstatten und Stornobeleg erstellen?`,
      )
    ) {
      return;
    }
    grundAbfragen(beleg.beleg, 'karte');
  }

  /** Karte: Rückerstattung über den mit Karte bezahlten Rest des eingefrorenen Originals. */
  async function karteRueckerstatten(original: BelegDetails): Promise<void> {
    const zahlungsreferenz = original.zahlungsreferenz;
    if (zahlungsreferenz === null) {
      return;
    }
    const summe = summeBrutto(original.positionen) - original.gutscheinCent;
    setFehler(null);
    try {
      const z = await api.erstatteZahlung(
        kassenId,
        zahlungsreferenz,
        summe,
        ladeAnmeldung(sessionStorage, kassenId)?.sitzung,
      );
      if (z.status === 'ausstehend') {
        setRueckerstattung(z); // Terminal im Laden führt sie aus – Dialog wartet
        return;
      }
      await rueckerstattungAbgeschlossen(z);
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }

  async function rueckerstattungAbgeschlossen(z: Zahlung): Promise<void> {
    setRueckerstattung(null);
    if (z.status !== 'erstattet') {
      setFehler(`Rückerstattung ${z.status} – es entsteht kein Stornobeleg`);
      return;
    }
    // R4: Stornobeleg zum Original des Auftrags, nicht zum gerade angezeigten Beleg
    const original = stornoAuftrag.current?.original ?? beleg?.beleg ?? null;
    if (original === null) {
      return;
    }
    setErstattet(z);
    await stornobelegErstellen(original, 'karte', z.zahlungsreferenz);
  }

  const stornierbar = beleg !== null && beleg.beleg.belegtyp === 'STANDARD';
  const zahlungsart = beleg?.beleg.zahlungsart ?? null;

  return (
    <>
      <div className="karte">
        <div className="zeile">
          <label>
            Belegnummer
            <input
              value={nummer}
              onChange={(e) => {
                setNummer(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  void laden();
                }
              }}
              style={{ minWidth: '16rem' }}
            />
          </label>
          <button
            className="taste"
            onClick={() => {
              void laden();
            }}
          >
            Anzeigen
          </button>
          <button
            className="taste zweit"
            onClick={() => {
              void suchen(nummer);
            }}
          >
            Suchen
          </button>
        </div>
        {fehler !== null && <p className="fehler">{fehler}</p>}
        {treffer.length > 0 && (
          <>
            <h3>
              {suchtext === null ? 'Letzte Belege' : `Treffer für „${suchtext}“`} ({trefferGesamt})
            </h3>
            <table className="positionen">
              <tbody>
                {treffer.map((t) => (
                  <tr key={t.belegnummer}>
                    <td>
                      <button
                        className="taste zweit"
                        style={{ minHeight: '2.2rem', padding: '0.2rem 0.6rem' }}
                        onClick={() => {
                          setNummer(t.belegnummer);
                          void laden(t.belegnummer);
                        }}
                      >
                        {t.belegnummer}
                      </button>
                    </td>
                    <td>
                      {new Date(t.zeitpunkt).toLocaleString('de-AT', { timeZone: 'Europe/Vienna' })}
                    </td>
                    <td>
                      {retoureKennzeichen(t) ?? t.sonderbeleg ?? t.belegtyp}
                      {t.zahlungsart !== null && ` · ${t.zahlungsart}`}
                      {t.signaturGestoert && ' · Ausfall'}
                      {t.bediener !== null && ` · ${t.bediener}`}
                      {t.notiz !== null && ` · ${t.notiz}`}
                    </td>
                    <td className="betrag">{formatiereEuro(t.summeCent)} €</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        {treffer.length === 0 && suchtext !== null && (
          <p className="hinweis">Keine Treffer für „{suchtext}“.</p>
        )}
        {meldung !== null && <p className="ok">{meldung}</p>}
        {erstattet !== null && beleg !== null && (
          <p className="warnung">
            Rückerstattung {erstattet.zahlungsreferenz} über {formatiereEuro(erstattet.betragCent)}{' '}
            € ist erfolgt, der Stornobeleg fehlt noch.{' '}
            <button
              className="taste zweit"
              disabled={belegLaeuft}
              onClick={() => {
                void stornobelegErstellen(
                  stornoAuftrag.current?.original ?? beleg.beleg,
                  'karte',
                  erstattet.zahlungsreferenz,
                );
              }}
            >
              Stornobeleg erneut erstellen
            </button>
          </p>
        )}
      </div>
      {beleg !== null && (
        <>
          <Belegansicht
            beleg={beleg.beleg}
            text={beleg.text}
            {...(props.bridgeDruck === undefined ? {} : { bridgeDruck: props.bridgeDruck })}
            {...(props.emailSenden === undefined ? {} : { emailSenden: props.emailSenden })}
            details={() => api.beleg(kassenId, beleg.beleg.belegnummer)}
            unternehmer={props.unternehmer ?? null}
            aktionen={
              stornierbar && storniertDurch !== null ? (
                <span className="hinweis">bereits storniert: {storniertDurch}</span>
              ) : stornierbar && zahlungsart !== null && erstattet === null ? (
                <button
                  className="taste gefahr"
                  disabled={
                    !aktuelleRechte(sessionStorage, kassenId).storno ||
                    stornoLaeuft ||
                    grundDialog !== null ||
                    rueckerstattung !== null
                  }
                  title={
                    aktuelleRechte(sessionStorage, kassenId).storno
                      ? undefined
                      : 'Kein Stornorecht für den angemeldeten Bediener'
                  }
                  onClick={() => {
                    if (zahlungsart === 'bar') {
                      barStornieren();
                    } else if (zahlungsart === 'gutschein') {
                      gutscheinStornieren();
                    } else {
                      karteStornieren();
                    }
                  }}
                >
                  {zahlungsart === 'bar'
                    ? 'Stornieren (bar)'
                    : zahlungsart === 'gutschein'
                      ? 'Stornieren (Gutschein gutschreiben)'
                      : 'Stornieren (Karte rückerstatten)'}
                </button>
              ) : undefined
            }
          />
          <div className="karte hinweis kein-druck">
            {istRetourenbeleg(beleg.beleg.belegnummer) && (
              <strong className="marke-rueckgabe">RÜCKGABE · </strong>
            )}
            {rueckgabe !== null && rueckgabe.nr === beleg.beleg.belegnummer && (
              <strong className="marke-rueckgabe">{rueckgabe.text} · </strong>
            )}
            {beleg.beleg.bediener !== null && `Bediener: ${beleg.beleg.bediener} · `}
            Zahlungsart: {zahlungsart ?? '–'}
            {beleg.beleg.zahlungsreferenz !== null && ` · Zahlung ${beleg.beleg.zahlungsreferenz}`}
            {beleg.beleg.gutscheinCent > 0 &&
              ` · davon Gutschein ${formatiereEuro(beleg.beleg.gutscheinCent)} €`}
            {beleg.beleg.gutscheine.length > 0 &&
              ` · Gutscheine: ${beleg.beleg.gutscheine
                .map(
                  (g) =>
                    `${g.code} ${g.art} ${formatiereEuro(g.betragCent)} € (Rest ${formatiereEuro(g.restNachherCent)} €)`,
                )
                .join(', ')}`}
            {props.bridgeDruck !== undefined &&
              beleg.beleg.gutscheine
                .filter((g) => g.art === 'ausgabe')
                .map((g) => (
                  <button
                    key={g.code}
                    className="taste zweit"
                    style={{ marginLeft: '0.5rem' }}
                    onClick={() => {
                      api
                        .gutscheinDruckauftrag(kassenId, g.code)
                        .then((a) => {
                          setMeldung(
                            `Gutscheinkarte ${g.code}: Druckauftrag ${a.auftragId} an die Bridge übergeben`,
                          );
                        })
                        .catch((e: unknown) => {
                          setFehler(e instanceof Error ? e.message : String(e));
                        });
                    }}
                  >
                    Gutscheinkarte {g.code} drucken (Bridge)
                  </button>
                ))}
            {zahlungsart === 'karte' &&
              beleg.beleg.belegtyp === 'STANDARD' &&
              ' · Beim Storno wird der Betrag über das Terminal rückerstattet; erst danach entsteht der Stornobeleg.'}
            {zahlungsart === 'karte' &&
              beleg.beleg.belegtyp === 'STORNO' &&
              ' · Gegenbuchung zur bestätigten Rückerstattung.'}
            <br />
            OCR: <code>{beleg.beleg.ocrCode}</code>
          </div>
        </>
      )}
      {grundDialog !== null && (
        <StornoGrundDialog
          belegnummer={grundDialog.original.belegnummer}
          start={stornoGrund.current ?? ''}
          onWeiter={(grund) => {
            void stornoFortsetzen(grundDialog, grund);
          }}
          onAbbrechen={() => {
            setGrundDialog(null);
          }}
        />
      )}
      {rueckerstattung !== null && (
        <ZahlungDialog
          api={api}
          kassenId={kassenId}
          zahlung={rueckerstattung}
          titel="Rückerstattung"
          onFertig={(z) => {
            void rueckerstattungAbgeschlossen(z);
          }}
        />
      )}
    </>
  );
}
